import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { collection, getDocs, doc, getDoc } from 'firebase/firestore'
import { db } from '../firebase'
import { useAuth } from './AuthContext'
import toast from 'react-hot-toast'

const BranchContext = createContext()

// Special "All Branches" option for business overview (owners only)
const ALL_BRANCHES_OPTION = {
  id: 'all-branches',
  name: 'All Branches',
  address: 'Business Overview',
  phone: '',
  isActive: true,
  isAllBranches: true,
}

export function useBranch() {
  const context = useContext(BranchContext)
  if (!context) {
    throw new Error('useBranch must be used within a BranchProvider')
  }
  return context
}

function BranchProvider({ children }) {
  const { currentUser, role, branch: userBranch } = useAuth()
  const [branches, setBranches] = useState([])
  const [currentBranch, setCurrentBranch] = useState(null)
  const [loading, setLoading] = useState(true)

  const clearBranchState = useCallback(() => {
    setBranches([])
    setCurrentBranch(null)
  }, [])

  const refreshBranches = useCallback(async () => {
    if (!currentUser || !role) {
      clearBranchState()
      setLoading(false)
      return
    }

    // Staff / managers must always use their assigned branch — never owner localStorage.
    if (role !== 'owner') {
      try {
        localStorage.removeItem('selectedBranch')
      } catch {
        /* ignore */
      }

      if (!userBranch) {
        clearBranchState()
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const branchSnap = await getDoc(doc(db, 'branches', userBranch))
        if (!branchSnap.exists()) {
          console.error('[BranchContext] Assigned branch not found:', userBranch)
          clearBranchState()
          toast.error('Your assigned branch was not found. Contact an administrator.')
          return
        }

        const branchData = { id: branchSnap.id, ...branchSnap.data() }
        setBranches([branchData])
        setCurrentBranch(branchData)
      } catch (error) {
        console.error('Error fetching assigned branch:', error)
        clearBranchState()
        toast.error('Failed to load your branch')
      } finally {
        setLoading(false)
      }
      return
    }

    // Owners: all branches + optional All Branches overview
    try {
      setLoading(true)
      const snapshot = await getDocs(collection(db, 'branches'))
      const branchesData = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }))

      const availableBranches = [ALL_BRANCHES_OPTION, ...branchesData]
      setBranches(availableBranches)

      const savedBranch = localStorage.getItem('selectedBranch')
      if (savedBranch === 'all-branches') {
        setCurrentBranch(ALL_BRANCHES_OPTION)
      } else if (savedBranch) {
        const branch = branchesData.find((b) => b.id === savedBranch)
        setCurrentBranch(branch || ALL_BRANCHES_OPTION)
      } else {
        setCurrentBranch(ALL_BRANCHES_OPTION)
      }
    } catch (error) {
      console.error('Error fetching branches:', error)
      clearBranchState()
      toast.error('Failed to load branches')
    } finally {
      setLoading(false)
    }
  }, [currentUser, role, userBranch, clearBranchState])

  useEffect(() => {
    refreshBranches()
  }, [refreshBranches])

  async function switchBranch(branchId) {
    if (role !== 'owner') {
      toast.error('You do not have permission to switch branches')
      return
    }

    try {
      if (branchId === 'all-branches') {
        setCurrentBranch(ALL_BRANCHES_OPTION)
        localStorage.setItem('selectedBranch', 'all-branches')
        toast.success('Switched to All Branches view')
        return
      }

      const branchDoc = await getDoc(doc(db, 'branches', branchId))
      if (branchDoc.exists()) {
        const branchData = { id: branchDoc.id, ...branchDoc.data() }
        setCurrentBranch(branchData)
        localStorage.setItem('selectedBranch', branchId)
        toast.success(`Switched to ${branchData.name}`)
      }
    } catch (error) {
      console.error('Error switching branch:', error)
      toast.error('Failed to switch branch')
    }
  }

  function getBranchById(branchId) {
    if (branchId === 'all-branches') {
      return ALL_BRANCHES_OPTION
    }
    return branches.find((branch) => branch.id === branchId && !branch.isAllBranches)
  }

  function canAccessBranch(branchId) {
    if (role === 'owner') return true
    if (branchId === 'all-branches') return false
    return userBranch === branchId
  }

  function isViewingAllBranches() {
    // Non-owners can never view all branches
    if (role !== 'owner') return false
    return currentBranch && currentBranch.isAllBranches === true
  }

  function getActualBranches() {
    return branches.filter((branch) => !branch.isAllBranches)
  }

  /**
   * Branch id that data queries must use.
   * Managers/staff are locked to Auth `userBranch` even if UI state is wrong.
   */
  function getEffectiveBranchId() {
    if (role === 'owner') {
      return currentBranch?.id || null
    }
    return userBranch || currentBranch?.id || null
  }

  const value = {
    branches,
    currentBranch,
    loading,
    refreshBranches,
    switchBranch,
    getBranchById,
    canAccessBranch,
    isViewingAllBranches,
    getActualBranches,
    getEffectiveBranchId,
    ALL_BRANCHES_OPTION,
    // Alias used by older modules
    selectedBranch: currentBranch,
  }

  return (
    <BranchContext.Provider value={value}>
      {children}
    </BranchContext.Provider>
  )
}

export { BranchProvider }
